import React, { useEffect } from 'react';
import { X } from 'react-feather';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import LogoSvg from '../../assets/icons/logo.svg';
import LoginHeroImage from '../../assets/images/login_hero.webp';
import { RouteNames } from '../../constants';
import styles from './LoginPrompt.module.scss';

/**
 * Shared "Log in to continue" popup — the two-column design first built
 * for CustomerCouponCard (photo + headline on the left, logo / copy /
 * Sign in + Signup on the right). Portaled to document.body so a parent's
 * transform (e.g. a card's hover-scale) can't throw off position:fixed.
 *
 * Every click inside stops propagating so a clickable parent (a coupon
 * card that navigates on click) doesn't also fire through the portal.
 *
 * Props:
 *   isOpen   — show / hide
 *   onClose  — called on backdrop click, close button, Esc, or before navigating
 *   message  — body copy under the title
 */
// Copy for every favourite / follow gate — import it rather than retyping it.
export const FAVOURITE_PROMPT_MESSAGE = 'Create a free account to save favourites and follow your favourite businesses.';

const LoginPrompt = ({ isOpen, onClose, title = 'Log in to continue', message = FAVOURITE_PROMPT_MESSAGE }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const go = route => {
    onClose();
    navigate(route);
  };

  return createPortal(
    <div
      role="presentation"
      className={styles.overlay}
      onClick={e => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-prompt-title"
        className={styles.modal}
        onClick={e => e.stopPropagation()}
      >
        <button type="button" aria-label="Close" className={styles.close} onClick={onClose}>
          <X size={30} />
        </button>

        <div className={styles.imageCol}>
          <div className={styles.imageFrame}>
            <img src={LoginHeroImage} alt="" className={styles.image} />
            <div className={styles.imageScrim} />
            <h3 className={styles.headline}>
              Turn Offers into <span>Opportunities.</span>
            </h3>
          </div>
        </div>

        <div className={styles.contentCol}>
          <img src={LogoSvg} alt="My Couponbook" className={styles.logo} />
          <h2 id="login-prompt-title" className={styles.title}>
            {title}
          </h2>
          <p className={styles.desc}>{message}</p>
          <button type="button" className={styles.btnPrimary} onClick={() => go(RouteNames.SIGN_IN)}>
            Sign in
          </button>
          <button type="button" className={styles.btnSecondary} onClick={() => go(RouteNames.SIGN_UP)}>
            Signup
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LoginPrompt;
